import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { and, eq, gt } from 'ember-truth-helpers';
import { t } from 'ember-intl';
import can from 'ember-can/helpers/can';
import {
  HdsPageHeader,
  HdsDropdown,
  HdsTextBody,
  HdsTextDisplay,
  HdsButton,
  HdsAlert,
} from '@hashicorp/design-system-components/components';
import { CoreLayoutMain, BreadcrumbsContainer, Flex } from 'core/components';
import { boxPadding } from 'core/modifiers';
import SummaryCard from 'hcp/components/iam/groups/summary-card';
import ListTable from 'hcp/components/iam/groups/members/list-table';
import AddMembers from 'hcp/components/iam/groups/stepper/add-members';
import DeleteGroup from 'hcp/components/iam/groups/modals/delete-group';

/**
 *
 * `PageGroupsOrganizationDetail` shows summary details of a group, and it's members.
 *
 *
 * ```
 * <Page::Groups::Organization::Detail
 *   @group={{foo}}
 *   @rawGroupPolicy={{bar}}
 *   @members={{array bar baz}}
 *   @groupLevelPermissions={{array bar baz}}
 *   @eligibleMembers={{array bar baz}}
 *   @organization={{organization}}
 *   @scimEnabled={{false}}
 *   @truncatedResourceName='my-awesome-...'
 *   @isProjectContext={{true}}
 * />
 * ```
 *
 * @class PageGroupsOrganizationDetail
 *
 */
export default class PageGroupsOrganizationDetail extends Component {
  /**
   * @argument group
   * @type {Object}
   */
  /**
   * @argument rawGroupPolicy
   * @type {<Object>}
   */
  /**
   * @argument members
   * @type {Array<Object>}
   */
  /**
   * @argument groupLevelPermissions
   * @type {Array<String>}
   */
  /**
   * @argument eligibleMembers
   * @type {Array<Object>}
   */
  /**
   * @argument organization
   * @type {Object}
   */
  /**
   * @argument scimEnabled
   * @type {Boolean}
   */
  /**
   * @argument truncatedResourceName
   * @type {String}
   */
  /**
   * @argument isProjectContext
   * @type {Boolean}
   */
  @service abilities;

  @tracked isActive = false;

  get canUpdateGroup() {
    const { group, groupLevelPermissions } = this.args;

    if (!groupLevelPermissions || !group) {
      return false;
    }
    return this.abilities.can('update group', group, {
      groupLevelPermissions,
    });
  }

  get shouldShowAddMembers() {
    const { eligibleMembers, group, groupLevelPermissions } = this.args;

    if (!groupLevelPermissions || !group || !eligibleMembers) {
      return false;
    }

    const hasEligibleMembers = eligibleMembers.length > 0;

    return (
      hasEligibleMembers &&
      this.abilities.can('update-membership in group', group, {
        groupLevelPermissions,
      })
    );
  }

  toggleDeleteModal = () => {
    this.isActive = !this.isActive;
  };
  <template>
    {{#let
      (and (eq @scimEnabled true) (eq @group.scimSynchronized true))
      as |scimManaged|
    }}
      <CoreLayoutMain>
        <:header>
          <HdsPageHeader as |PH|>
            <PH.Breadcrumb>
              <BreadcrumbsContainer />
            </PH.Breadcrumb>

            <PH.IconTile @icon="users" />
            <PH.Title>
              {{@group.displayName}}
            </PH.Title>
            <PH.Actions>
              {{#if this.canUpdateGroup}}
                <HdsDropdown @listPosition="bottom-right" as |DD|>
                  <DD.ToggleButton
                    @color="secondary"
                    @text={{t "components.form.manage"}}
                    data-test-groups-organization-detail-actions-dropdown
                  />
                  <DD.Interactive
                    @icon="edit"
                    @route="cloud.access-control.role-assignments.detail.edit"
                    @model={{@group.resourceId}}
                    @text={{t
                      "components.page.access-control.groups.detail.actions.edit-role-assignments"
                    }}
                    data-test-groups-organization-detail-actions-dropdown-edit-assignment
                  />
                  {{#if scimManaged}}
                    <DD.Separator />
                    <DD.Generic {{boxPadding "xs sm xs md"}}>
                      <HdsTextBody
                        @color="primary"
                        @size="200"
                        data-test-groups-organization-detail-manage-scim-group-text
                      >
                        {{t
                          "components.page.access-control.groups.detail.actions.manage-scim-group"
                        }}
                      </HdsTextBody>
                    </DD.Generic>
                  {{else}}
                    <DD.Interactive
                      @icon="edit"
                      @route="cloud.access-control.groups.detail.edit"
                      @model={{@group.resourceName}}
                      @text={{t
                        "components.page.access-control.groups.detail.actions.edit"
                      }}
                      data-test-groups-organization-detail-actions-dropdown-edit-group-details
                    />
                    {{#if this.shouldShowAddMembers}}
                      <DD.Interactive
                        @icon="users"
                        @route="cloud.access-control.groups.detail.members.add"
                        @text={{t
                          "components.page.access-control.groups.detail.members.actions.add-group-members"
                        }}
                        data-test-groups-organization-detail-actions-dropdown-add-group-members
                      />
                    {{/if}}
                    {{#if (can "delete group")}}
                      <DD.Separator />
                      <DD.Interactive
                        @color="critical"
                        @icon="trash"
                        @text={{t
                          "components.page.access-control.groups.detail.actions.delete"
                        }}
                        {{on "click" this.toggleDeleteModal}}
                        data-test-groups-organization-detail-actions-dropdown-delete-group
                      />
                    {{/if}}
                  {{/if}}
                </HdsDropdown>
              {{/if}}
            </PH.Actions>
            <PH.Description>
              {{@group.description}}
            </PH.Description>
          </HdsPageHeader>
        </:header>
        <:alert>
          <SummaryCard
            @group={{@group}}
            @scimEnabled={{@scimEnabled}}
            @ssoEnabled={{@ssoEnabled}}
            @truncatedResourceName={{@truncatedResourceName}}
          />
        </:alert>
        <:body>
          <Flex @direction="column" @gap="sm" {{boxPadding "0 0 md 0"}}>
            <HdsTextDisplay @tag="h2" @size="400" @weight="bold">
              {{t "components.page.access-control.groups.detail.members.title"}}
            </HdsTextDisplay>
            {{#if
              (and
                (gt @members.length 0)
                (and
                  (can
                    "update-membership in group"
                    @group
                    groupLevelPermissions=@groupLevelPermissions
                  )
                  (gt @eligibleMembers.length 0)
                  (eq scimManaged false)
                )
              )
            }}
              <Flex @alignItems="center" @direction="row" @justifyContent="end">
                <HdsButton
                  @route="cloud.access-control.groups.detail.members.add"
                  @text={{t
                    "components.page.access-control.groups.detail.members.actions.add-group-members"
                  }}
                  @icon="plus"
                  @iconPosition="leading"
                  data-test-groups-organization-detail-add-members-cta
                />
              </Flex>
            {{/if}}
          </Flex>
          {{#if @members.length}}
            <Flex @direction="row">
              <Flex @grow={{1}}>
                <ListTable
                  @group={{@group}}
                  @scimEnabled={{@scimEnabled}}
                  @isProjectContext={{@isProjectContext}}
                  @members={{@members}}
                  @groupLevelPermissions={{@groupLevelPermissions}}
                  @rawGroupPolicy={{@rawGroupPolicy}}
                />
              </Flex>
            </Flex>
          {{else}}
            {{#if
              (and
                (can
                  "update groups" groupLevelPermissions=@groupLevelPermissions
                )
                (eq scimManaged false)
              )
            }}
              <Flex @direction="row">
                <Flex data-test-groups-organization-empty-state-add-members-cta>
                  <AddMembers />
                </Flex>
                <Flex />
              </Flex>
            {{else}}
              <HdsAlert
                @type="inline"
                data-test-groups-organization-alert
                as |A|
              >
                <A.Title>
                  {{t
                    "components.page.access-control.groups.detail.members.empty.title"
                  }}
                </A.Title>
                <A.Description>
                  {{t
                    "components.page.access-control.groups.detail.members.empty.description"
                  }}
                </A.Description>
              </HdsAlert>
            {{/if}}
          {{/if}}
        </:body>
      </CoreLayoutMain>
    {{/let}}
    <DeleteGroup
      @isActive={{this.isActive}}
      @closeModal={{this.toggleDeleteModal}}
      @group={{@group}}
      @groupName={{@group.displayName}}
    />
  </template>
}
